import { AlertDiamondIcon, InboxIcon, RotateRight01Icon, SquareLock02Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Accessible loading label, announced once for a skeleton group. Omit for decorative pieces. */
  label?: string;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton({ className, label, ...props }, ref) {
  return (
    <div
      ref={ref}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('animate-pulse rounded-control bg-surface-subtle', className)}
      {...props}
    />
  );
});

/** Stacked text-line skeletons with a single accessible label. */
export function SkeletonText({ lines = 3, label = 'Cargando…', className }: { lines?: number; label?: string; className?: string }) {
  return (
    <div role="status" aria-label={label} className={cn('grid gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

interface StateShellProps {
  title: string;
  description?: React.ReactNode;
  icon: React.ReactNode;
  iconClass: string;
  action?: React.ReactNode;
  compact?: boolean;
  className?: string;
  role?: string;
  children?: React.ReactNode;
}

function StateShell({ title, description, icon, iconClass, action, compact, className, role, children }: StateShellProps) {
  return (
    <div role={role} className={cn('flex flex-col items-center justify-center text-center', compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-10', className)}>
      <div className={cn('flex size-11 items-center justify-center rounded-pill [&_svg]:size-5', iconClass)}>{icon}</div>
      <div className="grid max-w-md gap-1">
        <p className="text-body-lg font-semibold text-text-primary">{title}</p>
        {description ? <div className="text-body text-text-secondary">{description}</div> : null}
        {children}
      </div>
      {action ? <div className="mt-1 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
  className?: string;
}

export function EmptyState({ icon, ...props }: EmptyStateProps) {
  return <StateShell icon={icon ?? <Icon icon={InboxIcon} aria-hidden />} iconClass="bg-surface-subtle text-text-secondary" {...props} />;
}

export interface ErrorStateProps {
  title?: string;
  description?: React.ReactNode;
  /** Technical reference (error code / request id). Never include message content or secrets. */
  reference?: string;
  /** Shows a "Reintentar" button. */
  onRetry?: () => void;
  retrying?: boolean;
  action?: React.ReactNode;
  compact?: boolean;
  className?: string;
}

export function ErrorState({
  title = 'No pudimos cargar esta información',
  description = 'Revisa tu conexión e inténtalo de nuevo.',
  reference,
  onRetry,
  retrying,
  action,
  compact,
  className,
}: ErrorStateProps) {
  return (
    <StateShell
      role="alert"
      title={title}
      description={description}
      icon={<Icon icon={AlertDiamondIcon} aria-hidden />}
      iconClass="bg-danger-bg text-danger"
      compact={compact}
      className={className}
      action={
        onRetry || action ? (
          <>
            {onRetry ? (
              <Button variant="secondary" onClick={onRetry} loading={retrying} leadingIcon={<Icon icon={RotateRight01Icon} aria-hidden />}>
                Reintentar
              </Button>
            ) : null}
            {action}
          </>
        ) : undefined
      }
    >
      {reference ? <p className="ef-tabular text-meta text-text-secondary">Referencia: {reference}</p> : null}
    </StateShell>
  );
}

export interface PermissionDeniedProps {
  title?: string;
  description?: React.ReactNode;
  /** Role needed, e.g. "Administrador". */
  requiredRole?: string;
  action?: React.ReactNode;
  compact?: boolean;
  className?: string;
}

export function PermissionDenied({
  title = 'No tienes acceso a esta sección',
  description,
  requiredRole,
  action,
  compact,
  className,
}: PermissionDeniedProps) {
  return (
    <StateShell
      title={title}
      description={
        description ??
        (requiredRole
          ? `Se requiere el rol ${requiredRole} o superior. Pide acceso a un administrador del espacio.`
          : 'Pide acceso a un administrador del espacio.')
      }
      icon={<Icon icon={SquareLock02Icon} aria-hidden />}
      iconClass="bg-warning-bg text-warning"
      action={action}
      compact={compact}
      className={className}
    />
  );
}

/** Visually hidden but available to assistive technology. */
export function VisuallyHidden({ children, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className="sr-only" {...props}>
      {children}
    </span>
  );
}
